import { Box, IconButton, Typography } from '@mui/material';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { DRAWER_WIDTH, POPULATION_MODE } from 'common/Constant';
import StyledTooltip from 'common/StyledTooltip';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getPersonIdDisplayLabel } from './personIdUtils';
import { fetchAvailableDateBoundsAndPlanYear, fetchCohortOptions } from 'features/frontoffice/actions/actions';
import { defaultAppliedFilterValues } from './globalFiltersDefaults';
import { setGlobalFilters } from './globalFiltersReducer';

const timeWindowLabels = {
  R12: 'Rolling 12 Months',
  PYTD_VS_PPYTD: 'Plan YTD vs Prior Plan YTD',
  PYTD_VS_PPY: 'Plan YTD vs Prior Plan Year',
  CUSTOM: 'Custom Range',
};

const dateBasisLabels = {
  incurred: 'Incurred Date',
  paid: 'Paid Date',
};

const shouldShowBanner = (pathname) =>
  pathname === '/bi' || pathname === '/bi/' || pathname.startsWith('/bi/smart-cards') || pathname.startsWith('/bi/reports');

const chipBaseSx = {
  display: 'flex',
  height: '24px',
  padding: '8px 12px',
  alignItems: 'center',
  gap: '4px',
  borderRadius: '8px',
  border: '1px solid #71767A',
  background: '#FFF',
  boxSizing: 'border-box',
  whiteSpace: 'nowrap',
};

const chipLabelSx = {
  color: '#121A22',
  fontFamily: '"Noto Sans"',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '140%',
};

const chipValueSx = {
  color: '#121A22',
  fontFamily: '"Noto Sans"',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '140%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '220px',
};

const bannerLabelSx = {
  minWidth: '79px',
  height: '24px',
  flexDirection: 'column',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  fontFamily: '"Noto Sans"',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '140%',
};

const FilterChip = ({ label, value, tooltipLines }) => {
  const showTooltip = Array.isArray(tooltipLines) && tooltipLines.length > 0;
  const tooltipTitle = showTooltip
    ? tooltipLines.length === 1
      ? tooltipLines[0]
      : tooltipLines.join(', ')
    : '';

  const chipContent = (
    <Box sx={chipBaseSx}>
      <Typography component="span" sx={chipLabelSx}>
        {label}:
      </Typography>
      <Typography component="span" sx={chipValueSx}>
        {value}
      </Typography>
    </Box>
  );

  if (showTooltip) {
    return (
      <StyledTooltip
        title={tooltipTitle}
        placement="bottom"
      >
        {chipContent}
      </StyledTooltip>
    );
  }

  return chipContent;
};

const ActiveFiltersBanner = () => {
  const location = useLocation();
  const bannerRef = useRef(null);
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const appliedPersonIdValue = useSelector((state) => state.globalFilters?.applied?.personIdValue || '');
  const appliedPopulationMode = useSelector((state) => state.globalFilters?.applied?.populationMode || '');
  const appliedCohortLuid = useSelector((state) => state.globalFilters?.applied?.cohortLuid || '');
  const currentTenantCd = useSelector((state) => state.user?.currentTenantCd);
  const companies = useSelector((state) => state.user?.companies);
  const cohortOptions = useSelector((state) => state.fo?.cohortOptions);
  const cohortOptionsTenantCd = useSelector((state) => state.fo?.cohortOptionsTenantCd);
  const dispatch = useDispatch();

  const showBanner = shouldShowBanner(location.pathname);

  const updateHeight = useCallback(() => {
    const height = bannerRef.current ? bannerRef.current.offsetHeight : 0;
    document.documentElement.style.setProperty(
      '--active-filters-banner-height',
      showBanner ? `${height}px` : '0px',
    );
  }, [showBanner]);

  useEffect(() => {
    // The banner mounts before PrivateRoute rehydrates Redux from the JWT on a
    // hard refresh. Do not issue tenant-scoped requests until that context is
    // available; the effect will run again once it has been restored.
    if (!currentTenantCd) {
      return;
    }

    const fetchCohortOptionsAsync = async () => {
      await dispatch(fetchCohortOptions(currentTenantCd));
    };
    fetchCohortOptionsAsync();
    dispatch(fetchAvailableDateBoundsAndPlanYear(defaultAppliedFilterValues.dateBasis, defaultAppliedFilterValues.timeWindow, currentTenantCd, { hideSpinner: true }));
  }, [currentTenantCd, dispatch]);

   // When cohort options are refreshed (e.g. on mount or tenant change), check
  // whether the currently applied cohort still exists. If it has been deleted
  // from the backend, clear the applied population filters so the banner no
  // longer shows stale Person ID counts without the user having to open the
  // filters drawer.
  useEffect(() => {
    if (appliedPopulationMode !== POPULATION_MODE.COHORT || !appliedCohortLuid) {
      return;
    }
    if (!currentTenantCd || cohortOptionsTenantCd !== currentTenantCd) {
      return;
    }
    const isCohortStillAvailable = Object.values(cohortOptions || {})
      .flat()
      .some((option) => option?.value === String(appliedCohortLuid));
    if (!isCohortStillAvailable) {
      dispatch(setGlobalFilters({
        ...appliedFilters,
        populationMode: null,
        cohortLuid: null,
        cohortName: null,
        personIdValue: '',
      }));
    }
  }, [cohortOptions, cohortOptionsTenantCd, appliedPopulationMode, appliedCohortLuid, currentTenantCd, appliedFilters, dispatch]);

  useLayoutEffect(() => {
    updateHeight();
    return () => {
      document.documentElement.style.setProperty('--active-filters-banner-height', '0px');
    };
  }, [showBanner, appliedFilters, isMobileExpanded, updateHeight]);

  useEffect(() => {
    if (!showBanner || !bannerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(updateHeight);
    ro.observe(bannerRef.current);
    return () => ro.disconnect();
  }, [showBanner, updateHeight]);

  if (!showBanner) return null;

  // Remount the banner when population filters change so chip content cannot
  // remain stale across rapid drawer apply cycles.
  const bannerRenderKey = `${currentTenantCd || ''}::${appliedPopulationMode}::${appliedCohortLuid}::${appliedPersonIdValue}`;

  const currentTenant = Array.isArray(companies)
    ? companies.find((company) => company.code === currentTenantCd) || null
    : null;
  const tenantName = currentTenant?.name || null;
  const areTenantFiltersEnabled = currentTenant?.filtersEnabled !== false;
  const areDivisionsEnabled = currentTenant?.divisionsEnabled !== false;
  const arePlansEnabled = currentTenant?.plansEnabled !== false;
  const isDateBasisEnabled = currentTenant?.dateBasisEnabled !== false;
  const isDateRangeEnabled = currentTenant?.dateRangeEnabled !== false;
  const isPopulationEnabled = currentTenant?.cohortBuilderEnabled !== false;
  const hasEnabledTimeWindow = [
    currentTenant?.rolling12Enabled,
    currentTenant?.planYTDVsPriorPlanYTDEnabled,
    currentTenant?.planYTDVsPriorPlanYearEnabled,
    currentTenant?.customRangeEnabled,
  ].some((enabled) => enabled !== false);

  const chips = [];

  // Tenant chip — always show when tenant is known
  if (tenantName) {
    chips.push({ label: 'Tenant', value: tenantName });
  }

  if (areTenantFiltersEnabled) {
    // Person ID chip — only when IDs are entered
    if (isPopulationEnabled && appliedPersonIdValue.trim()) {
      const value = getPersonIdDisplayLabel(appliedPersonIdValue);
      if (value) {
        chips.push({ label: 'Person ID', value });
      }
    }

    // Division chip
    if (areDivisionsEnabled && appliedFilters?.isAllDivisionsSelected && !appliedFilters?.divisionsConstrainedByPlan) {
      const labels = Array.isArray(appliedFilters?.divisionLabels) ? appliedFilters.divisionLabels : [];
      chips.push({ label: 'Division', value: 'All Divisions', tooltipLines: labels.length > 0 ? labels : null });
    } else if (areDivisionsEnabled && Array.isArray(appliedFilters?.divisionLabels) && appliedFilters.divisionLabels.length > 0) {
      const labels = appliedFilters.divisionLabels;
      chips.push({
        label: 'Division',
        value: labels.length > 1 ? `${labels[0]} +${labels.length - 1} others` : labels[0],
        tooltipLines: labels,
      });
    }

    // Plan chip
    if (arePlansEnabled && appliedFilters?.isAllPlansSelected && !appliedFilters?.plansConstrainedByDivision) {
      const labels = Array.isArray(appliedFilters?.planLabels) ? appliedFilters.planLabels : [];
      chips.push({ label: 'Plan', value: 'All Plans', tooltipLines: labels.length > 0 ? labels : null });
    } else if (arePlansEnabled && Array.isArray(appliedFilters?.planLabels) && appliedFilters.planLabels.length > 0) {
      const labels = appliedFilters.planLabels;
      chips.push({
        label: 'Plan',
        value: labels.length > 1 ? `${labels[0]} +${labels.length - 1} others` : labels[0],
        tooltipLines: labels,
      });
    }

    // Date Basis chip — always when tenant filters are enabled
    if (isDateBasisEnabled) {
      chips.push({
        label: 'Date Basis',
        value: dateBasisLabels[appliedFilters?.dateBasis] || 'Paid Date',
      });
    }

    // Time Window chip — always when tenant filters are enabled
    if (isDateRangeEnabled && hasEnabledTimeWindow) {
      chips.push({
        label: 'Time Window',
        value: timeWindowLabels[appliedFilters?.timeWindow] || appliedFilters?.timeWindow || 'Rolling 12 Months',
      });
    }

    if (appliedFilters?.enablePriorComparison === false) {
        chips.push({
          label: 'Prior Period',
          value: 'Disabled'
        })
      }
    }

  return (
    <Box
      key={bannerRenderKey}
      ref={bannerRef}
      sx={{
        display: 'flex',
        width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
        ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
        padding: { xs: '16px', sm: '16px 48px' },
        alignItems: 'center',
        borderBottom: '1px solid #71767A',
        background: '#FFF',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'flex-start',
          gap: '8px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: { xs: '100%', sm: 'auto' },
            height: '24px',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              ...bannerLabelSx,
              display: { xs: 'flex', sm: 'none' },
              color: '#000',
            }}
          >
            Active Filters
          </Typography>
          <Typography
            sx={{
              ...bannerLabelSx,
              display: { xs: 'none', sm: 'flex' },
              color: '#121A22',
            }}
          >
            Active Filters:
          </Typography>
          <IconButton
            aria-expanded={isMobileExpanded}
            aria-label={`${isMobileExpanded ? 'Collapse' : 'Expand'} active filters`}
            disableRipple
            onClick={() => setIsMobileExpanded((expanded) => !expanded)}
            sx={{
              display: { xs: 'flex', sm: 'none' },
              width: '20px',
              height: '20px',
              padding: 0,
              color: '#001A47',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            {isMobileExpanded ? <IconChevronUp size={20} /> : <IconChevronDown size={20} />}
          </IconButton>
        </Box>
        <Box
          sx={{
            display: { xs: isMobileExpanded ? 'flex' : 'none', sm: 'flex' },
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {chips.map((chip) => (
            <FilterChip key={chip.label} label={chip.label} value={chip.value} tooltipLines={chip.tooltipLines} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ActiveFiltersBanner;
